import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "../lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Alert, AlertDescription } from "../components/ui/alert";
import { StatusBadge } from "../components/ui/status-badge";
import { PerformanceBar } from "../components/ui/performance-bar";
import { format, formatDistanceToNow } from "date-fns";
import { Save, History, FileText, FileCode, ArrowLeftRight, PlusCircle, Upload, RefreshCw, Plus, X, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import * as jsondiffpatch from "jsondiffpatch";
import { UpdateDeviceForm } from "./updateDevice";
import { addDevice, getDevices } from "../api/axios";
import type { DeviceFormData } from "../lib/device";
import AddSNMP from "./addSNMP";
import AddSSH from "./addSSH";

export default function ConfigManagement() {
  // State for device selection
  const [selectedDevice, setSelectedDevice] = useState<string>("");
  const [selectedSnapshot, setSelectedSnapshot] = useState<string>(""); 
  const [compareSnapshot, setCompareSnapshot] = useState<string>("");
  const [newConfigNote, setNewConfigNote] = useState<string>("");
  const [configContent, setConfigContent] = useState<string>("");
  const [showConfigEditor, setShowConfigEditor] = useState<boolean>(false);
  const [compareView, setCompareView] = useState<boolean>(false);
  const [diffHtml, setDiffHtml] = useState<string>("");
  const [showEditDevice, setShowEditDevice] = useState<boolean>(false);
  const [editDeviceId, setEditDeviceId] = useState<string>("");

  // Add Device Form State
  const [protocol, setProtocol] = useState<"snmp" | "ssh">("snmp");
  const [formData, setFormData] = useState<DeviceFormData>({
    name: "",
    hostname: "",
    ipAddress: "",
    manufacturer: "",
    deviceType: "server",
    description: "",
    location: "",
    group: "Default",
    status: "unknown",
    snmpCommunity: "public",
    snmpVersion: "2c",
    snmpPort: 161,
    snmpUsername: "",
    snmpAuthPassword: "",
    snmpPrivPassword: "",
    snmpAuthProtocol: "SHA",
    snmpPrivProtocol: "AES",
    snmpOid: "",
    tags: "",
    ssh: [{
      host: "",
      port: 22,
      username: "",
      enc_password: "",
      private_key: "",
      commands: [],
    }],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [submittedDeviceName, setSubmittedDeviceName] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tagList, setTagList] = useState<string[]>([]);
  
  // Get all devices
  const { data: devices = [], isLoading: isLoadingDevices, error: devicesError, refetch: refetchDevices } = useQuery({
    queryKey: ["/api/devices"],
    queryFn: getDevices,
  });
  
  // Get config snapshots for selected device
  const { data: snapshots = [], isLoading: isLoadingSnapshots } = useQuery({
    queryKey: ["/api/config-snapshots", selectedDevice],
    queryFn: () => {
      if (!selectedDevice) return Promise.resolve([]);
      return fetch(`/api/config-snapshots?deviceId=${selectedDevice}`).then(res => res.json());
    },
    enabled: !!selectedDevice,
  });
  
  // Get selected snapshot details
  const { data: snapshotDetails = null, isLoading: isLoadingSnapshotDetails } = useQuery({
    queryKey: ["/api/config-snapshots", selectedSnapshot],
    queryFn: () => {
      if (!selectedSnapshot) return Promise.resolve(null);
      return fetch(`/api/config-snapshots/${selectedSnapshot}`).then(res => res.json());
    },
    enabled: !!selectedSnapshot,
  });
  
  // Get compare snapshot details (for diff view)
  const { data: compareSnapshotDetails = null, isLoading: isLoadingCompareDetails } = useQuery({
    queryKey: ["/api/config-snapshots", compareSnapshot],
    queryFn: () => {
      if (!compareSnapshot) return Promise.resolve(null);
      return fetch(`/api/config-snapshots/${compareSnapshot}`).then(res => res.json());
    },
    enabled: !!compareSnapshot && compareView,
  });

  // Create new config snapshot
  const createSnapshotMutation = useMutation({
    mutationFn: (data: any) => {
      return fetch("/api/config-snapshots", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }).then(res => res.json());
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/config-snapshots", selectedDevice] });
      setNewConfigNote("");
      setConfigContent("");
      setShowConfigEditor(false);
    },
  });

  // Apply config to device
  const applyConfigMutation = useMutation({
    mutationFn: (data: any) => {
      return fetch(`/api/devices/${selectedDevice}/apply-config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }).then(res => res.json());
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/devices"] });
    },
  });

  // Handle device selection
  const handleDeviceSelect = (deviceId: string) => {
    setSelectedDevice(deviceId);
    setSelectedSnapshot("");
    setCompareSnapshot("");
    setCompareView(false);
    setShowConfigEditor(false);
  };

  // Handle snapshot selection
  const handleSnapshotSelect = (snapshotId: string) => {
    setSelectedSnapshot(snapshotId);
    if (compareView && !compareSnapshot) {
      setCompareSnapshot("");
    }
  };

  // Handle compare snapshot selection
  const handleCompareSelect = (snapshotId: string) => {
    setCompareSnapshot(snapshotId);
  };

  // Toggle compare view
  const toggleCompareView = () => {
    setCompareView(!compareView);
    if (!compareView) {
      if (!compareSnapshot && snapshots.length > 1) {
        const currentIndex = snapshots.findIndex((s: any) => s.id.toString() === selectedSnapshot);
        if (currentIndex > 0) {
          setCompareSnapshot(snapshots[currentIndex - 1].id.toString());
        }
      }
    } else {
      setCompareSnapshot("");
      setDiffHtml("");
    }
  };

  // Handle creating a new snapshot
  const handleCreateSnapshot = () => {
    if (!selectedDevice) return;
    
    createSnapshotMutation.mutate({
      deviceId: parseInt(selectedDevice),
      content: configContent,
      notes: newConfigNote,
    });
  };

  // Handle applying a config to a device
  const handleApplyConfig = () => {
    if (!selectedDevice || !selectedSnapshot) return;
    
    applyConfigMutation.mutate({
      snapshotId: parseInt(selectedSnapshot),
    });
  };

  // Handle showing config editor
  const handleNewConfig = () => {
    if (snapshotDetails && snapshotDetails.content) {
      setConfigContent(snapshotDetails.content);
    } else {
      setConfigContent("");
    }
    setShowConfigEditor(true);
  };

  // Generate diff between two snapshots
  const generateDiff = () => {
    if (!snapshotDetails || !compareSnapshotDetails) return;
    
    try {
      let oldConfig, newConfig;
      
      try {
        oldConfig = JSON.parse(compareSnapshotDetails.content);
      } catch (e) {
        oldConfig = compareSnapshotDetails.content;
      }
      
      try {
        newConfig = JSON.parse(snapshotDetails.content);
      } catch (e) {
        newConfig = snapshotDetails.content;
      }
      
      if (typeof oldConfig === "string" && typeof newConfig === "string") {
        const oldLines = oldConfig.split('\n');
        const newLines = newConfig.split('\n');
        
        let html = '<div class="diff-view">';
        const maxLen = Math.max(oldLines.length, newLines.length);
        for (let i = 0; i < maxLen; i++) {
          const oldLine = i < oldLines.length ? oldLines[i] : null;
          const newLine = i < newLines.length ? newLines[i] : null;
          
          if (oldLine === newLine) {
            if (oldLine !== null) {
              html += `<pre class="text-gray-500">  ${oldLine}</pre>`;
            }
          } else {
            if (oldLine !== null) {
              html += `<pre class="text-red-500">- ${oldLine}</pre>`;
            }
            if (newLine !== null) {
              html += `<pre class="text-green-500">+ ${newLine}</pre>`;
            }
          }
        }
        html += '</div>';
        setDiffHtml(html);
      } else {
        const diffpatcher = jsondiffpatch.create({});
        const delta = diffpatcher.diff(oldConfig, newConfig);
        let html = '<div class="diff-view">';
        html += '<pre>' + JSON.stringify(delta, null, 2) + '</pre>';
        html += '</div>';
        setDiffHtml(html);
      }
    } catch (e) {
      console.error("Error generating diff:", e);
      setDiffHtml('<p class="text-red-500">Error generating diff. The configurations may be in different formats.</p>');
    }
  };

  // Effect to generate diff when both snapshots are loaded
  if (compareView && snapshotDetails && compareSnapshotDetails && !diffHtml) {
    generateDiff();
  }

  // Formatting functions
  const formatDate = (date: string) => {
    try {
      return format(new Date(date), "PPpp");
    } catch (e) {
      return "Invalid date";
    }
  };

  // Get device name
  const getDeviceName = (deviceId: number | string) => {
    const device = devices.find((d: any) => d.id.toString() === deviceId.toString());
    return device ? device.name : `Device ${deviceId}`;
  };

  // Handle opening edit device dialog
  const handleEditDevice = (deviceId: string) => {
    setEditDeviceId(deviceId);
    setShowEditDevice(true);
  };

  // Handle closing edit device dialog
  const handleCloseEditDevice = () => {
    setShowEditDevice(false);
    setEditDeviceId("");
    queryClient.invalidateQueries({ queryKey: ["/api/devices"] });
    refetchDevices();
  };

  // Add Device Form Functions
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Device name is required";
    }

    if (!formData.hostname.trim()) {
      newErrors.hostname = "Hostname is required";
    }

    if (!formData.ipAddress.trim()) {
      newErrors.ipAddress = "IP address is required";
    }

    if (protocol === "snmp") {
      if (formData.snmpPort < 1 || formData.snmpPort > 65535) {
        newErrors.snmpPort = "Port must be between 1 and 65535";
      }

      if (formData.snmpVersion === "3") {
        if (!formData.snmpUsername?.trim()) {
          newErrors.snmpUsername = "Username is required for SNMP v3";
        }

        if (!formData.snmpAuthPassword?.trim()) {
          newErrors.snmpAuthPassword = "Authentication password is required for SNMP v3";
        } else if (formData.snmpAuthPassword.length < 8) {
          newErrors.snmpAuthPassword = "Authentication password must be at least 8 characters";
        }

        if (formData.snmpPrivPassword && formData.snmpPrivPassword.length < 8) {
          newErrors.snmpPrivPassword = "Privacy password must be at least 8 characters";
        }
      }
    } else if (protocol === "ssh") {
      const ssh = formData.ssh && formData.ssh[0];
      if (!ssh?.host?.trim()) newErrors.sshHost = "Host required";
      if (!ssh?.port || ssh.port < 1 || ssh.port > 65535) newErrors.sshPort = "Port must be between 1 and 65535";
      if (!ssh?.username?.trim()) newErrors.sshUsername = "Username required";
      if (!ssh?.enc_password?.trim() && !ssh?.private_key?.trim()) newErrors.sshPassword = "Password or Private Key required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddDeviceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      setSubmitStatus("error");
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const deviceData: any = {
        name: formData.name,
        hostname: formData.hostname,
        ipAddress: formData.ipAddress,
        type: formData.deviceType,
        group: formData.group,
        status: formData.status,
        tags: tagList,
        manufacturer: formData.manufacturer,
        description: formData.description,
        location: formData.location,
      };

      if (protocol === "snmp") {
        deviceData.snmp = [
          formData.snmpVersion === "3"
            ? {
                name: formData.name,
                version: formData.snmpVersion,
                port: formData.snmpPort,
                username: formData.snmpUsername,
                authentication_protocol: formData.snmpAuthProtocol,
                authentication_password: formData.snmpAuthPassword,
                encryption: formData.snmpPrivProtocol,
                encryption_password: formData.snmpPrivPassword,
                oid: formData.snmpOid,
              }
            : {
                name: formData.name,
                version: formData.snmpVersion,
                port: formData.snmpPort,
                community: formData.snmpCommunity,
                oid: formData.snmpOid,
              },
        ];
      } else if (protocol === "ssh") {
        if (formData.ssh && formData.ssh.length > 0 && formData.ssh[0].commands.length > 0) {
          deviceData.ssh = formData.ssh?.[0]?.commands.map((cmd: any) => ({
            host: formData.ssh?.[0]?.host,
            port: formData.ssh?.[0]?.port,
            username: formData.ssh?.[0]?.username,
            enc_password: formData.ssh?.[0]?.enc_password,
            private_key: formData.ssh?.[0]?.private_key,
            name: cmd.name,
            command: cmd.command
          })) || [];
        }
      }

      setSubmittedDeviceName(formData.name);
      const response = await addDevice(deviceData);
      if (response && response.success) {
        setSubmitStatus("success");
        // Reset form
        setFormData({
          name: "",
          hostname: "",
          ipAddress: "",
          manufacturer: "",
          deviceType: "server",
          description: "",
          location: "",
          group: "Default",
          status: "unknown",
          snmpCommunity: "public",
          snmpVersion: "2c",
          snmpPort: 161,
          snmpUsername: "",
          snmpAuthPassword: "",
          snmpPrivPassword: "",
          snmpAuthProtocol: "SHA",
          snmpPrivProtocol: "AES",
          snmpOid: "",
          tags: "",
          ssh: [{
            host: "",
            port: 22,
            username: "",
            enc_password: "",
            private_key: "",
            commands: [],
          }],
        });
        setTagList([]);
        setTagInput("");
        queryClient.invalidateQueries({ queryKey: ["/api/devices"] });
        refetchDevices();
        setTimeout(() => {
          setSubmitStatus("idle");
        }, 3000);
      } else {
        setSubmitStatus("error");
        setErrors(prev => ({ ...prev, submit: response?.message || "Failed to add device. Please check your configuration and try again." }));
      }
    } catch (error: any) {
      setSubmitStatus("error");
      const backendMsg = error?.response?.data?.message || "Failed to add device. Please check your configuration and try again.";
      setErrors(prev => ({ ...prev, submit: backendMsg }));
      console.error("Failed to add device:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof DeviceFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !tagList.includes(tagInput.trim())) {
      setTagList((prev) => [...prev, tagInput.trim()]);
      setTagInput("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTagList((prev) => prev.filter((tag) => tag !== tagToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag();
    }
  };

  const getDeviceIcon = (type: string) => {
    const iconMap: Record<string, string> = {
      "Network Router": "🌐",
      "Web Server": "🖥️",
      "Database Server": "💾",
      "Firewall": "🛡️",
      "Switch": "🔌",
    };
    return iconMap[type] || "📦";
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case "up":
        return "text-green-600";
      case "down":
        return "text-red-600";
      case "warning":
        return "text-yellow-600";
      default:
        return "text-gray-500";
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <h1 className="text-3xl font-bold">Configuration Management</h1>
      
      {/* Add Device Form */}
      <Card>
        <CardHeader>
          <CardTitle>Add New Device</CardTitle>
          <CardDescription>Configure a new device for network monitoring</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAddDeviceSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Device Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="e.g., Main Router"
                    className={errors.name ? "border-red-500" : ""}
                  />
                  {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="hostname">Hostname *</Label>
                  <Input
                    id="hostname"
                    value={formData.hostname}
                    onChange={(e) => handleInputChange("hostname", e.target.value)}
                    placeholder="e.g., router.company.com"
                    className={errors.hostname ? "border-red-500" : ""}
                  />
                  {errors.hostname && <p className="text-sm text-red-500">{errors.hostname}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ipAddress">IP Address *</Label>
                  <Input
                    id="ipAddress"
                    value={formData.ipAddress}
                    onChange={(e) => handleInputChange("ipAddress", e.target.value)}
                    placeholder="e.g., 192.168.1.1"
                    className={errors.ipAddress ? "border-red-500" : ""}
                  />
                  {errors.ipAddress && <p className="text-sm text-red-500">{errors.ipAddress}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="manufacturer">Manufacturer</Label>
                  <Input
                    id="manufacturer"
                    value={formData.manufacturer}
                    onChange={(e) => handleInputChange("manufacturer", e.target.value)}
                    placeholder="e.g., Palo Alto, Cisco"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="deviceType">Device Type</Label>
                  <Select value={formData.deviceType} onValueChange={(value) => handleInputChange("deviceType", value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="router">Router</SelectItem>
                      <SelectItem value="switch">Switch</SelectItem>
                      <SelectItem value="server">Server</SelectItem>
                      <SelectItem value="firewall">Firewall</SelectItem>
                      <SelectItem value="access-point">Access Point</SelectItem>
                      <SelectItem value="printer">Printer</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => handleInputChange("location", e.target.value)}
                    placeholder="e.g., Server Room A"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="group">Group</Label>
                  <Input
                    id="group"
                    value={formData.group}
                    onChange={(e) => handleInputChange("group", e.target.value)}
                    placeholder="e.g., Default, Core, Edge"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  placeholder="Optional description of the device..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder="Add tags..."
                  />
                  <Button type="button" onClick={addTag} size="icon" variant="outline">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {tagList.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {tagList.map((tag) => (
                      <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                        {tag}
                        <button type="button" onClick={() => removeTag(tag)} className="ml-1 hover:text-red-500">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* SNMP/SSH Protocol Selection */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Connection Protocol</h3>
              <div className="flex gap-4">
                <Button type="button" variant={protocol === 'snmp' ? "default" : "outline"} onClick={() => setProtocol("snmp")}>
                  SNMP
                </Button>
                <Button type="button" variant={protocol === 'ssh' ? "default" : "outline"} onClick={() => setProtocol("ssh")}>
                  SSH
                </Button>
              </div>
            </div>

            {/* SNMP or SSH Configuration */}
            <div className="space-y-4">
              {protocol === "snmp" ? (
                <AddSNMP formData={formData} setFormData={setFormData} errors={errors} />
              ) : (
                <AddSSH formData={formData} setFormData={setFormData} errors={errors} />
              )}
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-4">
              <Button type="submit" disabled={isSubmitting} className="bg-black text-white hover:bg-black/90">
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Add Device
              </Button>
            </div>
          </form>

          {submitStatus === "success" && (
            <Alert className="border-green-200 bg-green-50 mt-4">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Device "{submittedDeviceName}" has been created and added to the inventory.
              </AlertDescription>
            </Alert>
          )}

          {submitStatus === "error" && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{errors.submit || "Failed to add device. Please check your configuration and try again."}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Device List */}
      <Card>
        <CardHeader>
          <CardTitle>Device Inventory</CardTitle>
          <CardDescription>Manage and monitor all devices in your network</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingDevices ? (
            <div className="text-center py-4">Loading devices...</div>
          ) : devicesError ? (
            <div className="text-red-500 text-center py-4">Failed to load devices. Please try again later.</div>
          ) : devices.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="mx-auto h-12 w-12 mb-2 opacity-50" />
              <p>No devices found. Add your first device above.</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Device Name</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Group</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Performance</TableHead>
                    <TableHead>Last Check</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {devices.map((device: any) => (
                    <TableRow key={device._id || device.id}>
                      <TableCell>
                        <div className="flex items-center">
                          <span className={`${getStatusClass(device.status)} mr-2`}>
                            {getDeviceIcon(device.type)}
                          </span>
                          <div className="text-sm font-medium">
                            {device.name}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{device.ipAddress}</TableCell>
                      <TableCell>{device.type}</TableCell>
                      <TableCell>{device.group || "Default"}</TableCell>
                      <TableCell>
                        <StatusBadge
                          status={device.status as "up" | "down" | "warning" | "unknown"}
                        />
                      </TableCell>
                      <TableCell>
                        <PerformanceBar value={device.performance} />
                      </TableCell>
                      <TableCell>
                        {device.lastCheck ? formatDistanceToNow(new Date(device.lastCheck), { addSuffix: true }) : "Never"}
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          {/* <Button
                            variant="link"
                            className="text-primary hover:text-primary-dark p-0 h-auto"
                            asChild
                          >
                            <Link to={`/device/${device._id || device.id}`}>Details</Link>
                          </Button> */}
                          <Button
                            variant="link"
                            className="text-primary hover:text-primary-dark p-0 h-auto"
                            onClick={() => handleEditDevice((device._id || device.id).toString())}
                          >
                            Edit
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Configuration Snapshots Section */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Device Selection Panel */}
        <div className="md:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Select Device</CardTitle>
              <CardDescription>
                Select a device to manage configurations
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingDevices ? (
                <div className="text-center py-4">Loading devices...</div>
              ) : (
                <div className="space-y-2">
                  <RadioGroup 
                    value={selectedDevice} 
                    onValueChange={handleDeviceSelect}
                  >
                    {devices.map((device: any) => (
                      device && device.id !== undefined ? (
                        <div key={device.id} className="flex items-center space-x-2">
                          <RadioGroupItem value={device.id.toString()} id={`device-${device.id}`} />
                          <Label htmlFor={`device-${device.id}`} className="cursor-pointer flex-1">
                            <div>{device.name}</div>
                            <div className="text-xs text-muted-foreground">{device.ipAddress}</div>
                          </Label>
                        </div>
                      ) : null
                    ))}
                  </RadioGroup>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Configuration Snapshots Panel */}
        <div className="md:col-span-3">
          {!selectedDevice ? (
            <Card>
              <CardContent className="pt-6 text-center text-muted-foreground">
                <FileText className="mx-auto h-12 w-12 mb-2 opacity-50" />
                <p>Select a device to view its configuration history</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Configuration Snapshots</CardTitle>
                      <CardDescription>
                        History of configuration changes for {getDeviceName(selectedDevice)}
                      </CardDescription>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={toggleCompareView}
                        disabled={!selectedSnapshot || snapshots.length < 2}
                      >
                        <ArrowLeftRight className="mr-2 h-4 w-4" />
                        {compareView ? "Exit Compare" : "Compare"}
                      </Button>
                      
                      <Button 
                        variant="default" 
                        size="sm"
                        onClick={handleNewConfig}
                      >
                        <PlusCircle className="mr-2 h-4 w-4" />
                        New Config
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {isLoadingSnapshots ? (
                    <div className="text-center py-4">Loading configurations...</div>
                  ) : snapshots.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground">
                      <History className="mx-auto h-12 w-12 mb-2 opacity-50" />
                      <p>No configuration snapshots found</p>
                      <p className="text-sm mt-1">Create your first configuration snapshot</p>
                    </div>
                  ) : (
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            {compareView && <TableHead className="w-[80px]">Compare</TableHead>}
                            <TableHead className="w-[80px]">Select</TableHead>
                            <TableHead>Version</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead>Notes</TableHead>
                            <TableHead className="w-[100px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {snapshots.map((snapshot: any, index: number) => (
                            <TableRow key={snapshot.id}>
                              {compareView && (
                                <TableCell>
                                  <RadioGroup 
                                    value={compareSnapshot} 
                                    onValueChange={handleCompareSelect}
                                    disabled={snapshot.id.toString() === selectedSnapshot}
                                  >
                                    <RadioGroupItem 
                                      value={snapshot.id.toString()} 
                                      id={`compare-${snapshot.id}`}
                                      disabled={snapshot.id.toString() === selectedSnapshot}
                                    />
                                  </RadioGroup>
                                </TableCell>
                              )}
                              <TableCell>
                                <RadioGroup 
                                  value={selectedSnapshot} 
                                  onValueChange={handleSnapshotSelect}
                                >
                                  <RadioGroupItem 
                                    value={snapshot.id.toString()} 
                                    id={`snapshot-${snapshot.id}`}
                                  />
                                </RadioGroup>
                              </TableCell>
                              <TableCell>
                                <div className="font-medium">
                                  v{snapshots.length - index}
                                </div>
                                {index === 0 && (
                                  <Badge variant="outline" className="mt-1">Latest</Badge>
                                )}
                              </TableCell>
                              <TableCell>{formatDate(snapshot.timestamp)}</TableCell>
                              <TableCell>
                                <div className="text-sm text-muted-foreground max-w-[300px] truncate">
                                  {snapshot.notes || "No notes"}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleSnapshotSelect(snapshot.id.toString())}
                                >
                                  <FileCode className="h-4 w-4" />
                                  <span className="sr-only">View</span>
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Snapshot Details or Compare View */}
              {selectedSnapshot && (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        {compareView ? (
                          <CardTitle>Configuration Comparison</CardTitle>
                        ) : (
                          <CardTitle>Configuration Details</CardTitle>
                        )}
                        <CardDescription>
                          {compareView ? (
                            <>
                              Comparing v{snapshots.findIndex((s: any) => s.id.toString() === selectedSnapshot) + 1} with 
                              v{snapshots.findIndex((s: any) => s.id.toString() === compareSnapshot) + 1}
                            </>
                          ) : (
                            <>
                              Snapshot from {snapshotDetails && formatDate(snapshotDetails.timestamp)}
                            </>
                          )}
                        </CardDescription>
                      </div>
                      
                      {!compareView && (
                        <div>
                          <Button 
                            variant="default" 
                            size="sm"
                            onClick={handleApplyConfig}
                            disabled={applyConfigMutation.isPending}
                          >
                            {applyConfigMutation.isPending ? (
                              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Upload className="mr-2 h-4 w-4" />
                            )}
                            Apply to Device
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {compareView ? (
                      isLoadingCompareDetails || isLoadingSnapshotDetails ? (
                        <div className="text-center py-4">Loading configurations...</div>
                      ) : !diffHtml ? (
                        <div className="text-center py-4">
                          Select two different snapshots to compare
                        </div>
                      ) : (
                        <div className="border rounded-md p-4 bg-muted/30">
                          <div 
                            className="diff-container"
                            dangerouslySetInnerHTML={{ __html: diffHtml }}
                          />
                          <style>{`
                            .diff-container :global(.jsondiffpatch-added) {
                              background-color: #e6ffec;
                              color: #24292f;
                            }
                            .diff-container :global(.jsondiffpatch-deleted) {
                              background-color: #ffebe9;
                              color: #24292f;
                            }
                            .diff-container :global(.jsondiffpatch-modified) {
                              background-color: #fafafa;
                            }
                            :global(.dark) .diff-container :global(.jsondiffpatch-added) {
                              background-color: rgba(46, 160, 67, 0.15);
                              color: #adbac7;
                            }
                            :global(.dark) .diff-container :global(.jsondiffpatch-deleted) {
                              background-color: rgba(248, 81, 73, 0.15);
                              color: #adbac7;
                            }
                            :global(.dark) .diff-container :global(.jsondiffpatch-modified) {
                              background-color: #1e1e1e;
                            }
                          `}</style>
                        </div>
                      )
                    ) : isLoadingSnapshotDetails ? (
                      <div className="text-center py-4">Loading configuration...</div>
                    ) : !snapshotDetails ? (
                      <div className="text-center py-4">No configuration details available</div>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <h3 className="text-sm font-medium mb-2">Notes:</h3>
                          <p className="text-sm text-muted-foreground">
                            {snapshotDetails.notes || "No notes for this configuration version"}
                          </p>
                        </div>
                        
                        <div>
                          <h3 className="text-sm font-medium mb-2">Configuration:</h3>
                          <pre className="p-4 rounded-md bg-muted text-sm overflow-auto max-h-[400px]">
                            {snapshotDetails.content || "Empty configuration"}
                          </pre>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Config Editor Dialog */}
              {showConfigEditor && (
                <Dialog open={showConfigEditor} onOpenChange={setShowConfigEditor}>
                  <DialogContent className="sm:max-w-[800px]">
                    <DialogHeader>
                      <DialogTitle>Create New Configuration</DialogTitle>
                      <DialogDescription>
                        Save a new configuration snapshot for {getDeviceName(selectedDevice)}
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="config-notes">Notes (Optional)</Label>
                        <Textarea 
                          id="config-notes" 
                          placeholder="Enter notes about this configuration change"
                          value={newConfigNote}
                          onChange={(e) => setNewConfigNote(e.target.value)}
                          rows={2}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="config-content">Configuration Content</Label>
                        <Textarea 
                          id="config-content" 
                          placeholder="Paste or enter the configuration content"
                          value={configContent}
                          onChange={(e) => setConfigContent(e.target.value)}
                          rows={15}
                          className="font-mono text-sm"
                        />
                      </div>
                    </div>
                    
                    <DialogFooter>
                      <Button 
                        variant="outline" 
                        onClick={() => setShowConfigEditor(false)}
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleCreateSnapshot}
                        disabled={!configContent || createSnapshotMutation.isPending}
                      >
                        {createSnapshotMutation.isPending ? (
                          <>
                            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="mr-2 h-4 w-4" />
                            Save Configuration
                          </>
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit Device Dialog */}
      <Dialog open={showEditDevice} onOpenChange={setShowEditDevice}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Device</DialogTitle>
            <DialogDescription>
              Update device configuration
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {editDeviceId && (
              <div className="max-w-4xl">
                <UpdateDeviceForm deviceId={editDeviceId} onClose={handleCloseEditDevice} />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
